// VerifyReceiptPage — Vérification publique d'authenticité d'un reçu fiscal AYA (scan QR).
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, ShieldX, Search, Camera, X } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";

const API = process.env.REACT_APP_BACKEND_URL || "";
const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;

// Extrait le numéro de document depuis un QR : URL /verifier-recu?n=…,
// format pipe AYA|RNE|NUMÉRO|… ou numéro brut FNE/RNE/AVR-….
function parseQrContent(text) {
  const s = String(text || "").trim();
  try {
    const u = new URL(s);
    const n = u.searchParams.get("n");
    if (n) return n.trim();
  } catch { /* pas une URL */ }
  const parts = s.split("|");
  if (parts[0] === "AYA" && parts[2]) return parts[2].trim();
  if (/^(FNE|RNE|AVR)-/i.test(s)) return s;
  return null;
}

const TYPE_LABEL = { FNE: "Facture Normalisée Électronique", RNE: "Reçu Normalisé Électronique", CREDIT_NOTE: "Avoir fiscal" };
const TYPE_COLOR = { FNE: "#0E7A4F", RNE: "#1D4ED8", CREDIT_NOTE: "#B45309" };

const S = {
  page: { minHeight: "100vh", background: "#04150D", color: "#ECF5EE", fontFamily: "'Inter', system-ui, sans-serif", padding: "28px 16px 60px" },
  wrap: { maxWidth: 520, margin: "0 auto" },
  card: { background: "rgba(6,34,22,0.85)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 16, padding: 18, marginTop: 14 },
  input: { flex: 1, background: "rgba(4,21,13,0.9)", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 10, padding: "11px 13px", color: "#ECF5EE", fontSize: 13, outline: "none" },
  btn: { display: "flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", border: "none", borderRadius: 10, padding: "11px 16px", fontWeight: 800, fontSize: 12.5, cursor: "pointer" },
  row: { display: "flex", justifyContent: "space-between", gap: 10, padding: "7px 0", borderBottom: "1px solid rgba(16,185,129,0.12)", fontSize: 12.5 },
  label: { color: "#9DB4A6" },
};

export default function VerifyReceiptPage() {
  const [params] = useSearchParams();
  const [number, setNumber] = useState(params.get("n") || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const scannerRef = useRef(null);

  const verify = useCallback(async (n) => {
    const num = String(n || "").trim();
    if (!num) return;
    setLoading(true); setResult(null);
    try {
      const r = await fetch(`${API}/api/v3/fiscal/verify/${encodeURIComponent(num)}`);
      const d = await r.json();
      setResult(d.ok ? d : { valid: false });
    } catch { setResult({ valid: false, error: true }); }
    setLoading(false);
  }, []);

  const stopScanner = useCallback(async () => {
    setScanning(false);
    const sc = scannerRef.current;
    scannerRef.current = null;
    if (sc) { try { await sc.stop(); sc.clear(); } catch { /* déjà arrêté */ } }
  }, []);

  const startScanner = useCallback(async () => {
    setScanError(null);
    setScanning(true);
    await new Promise((r) => setTimeout(r, 60)); // laisse le conteneur se monter
    try {
      const sc = new Html5Qrcode("aya-qr-reader");
      scannerRef.current = sc;
      await sc.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (decoded) => {
          const num = parseQrContent(decoded);
          if (num) {
            setNumber(num);
            stopScanner();
            verify(num);
          }
        },
        () => { /* frame sans QR — silencieux */ },
      );
    } catch (e) {
      setScanning(false);
      setScanError(
        String(e).toLowerCase().includes("permission") || String(e).toLowerCase().includes("notallowed")
          ? "Accès caméra refusé — autorisez la caméra dans votre navigateur puis réessayez."
          : "Caméra indisponible sur cet appareil. Saisissez le numéro manuellement."
      );
    }
  }, [stopScanner, verify]);

  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  useEffect(() => {
    const n = params.get("n");
    if (n) verify(n);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doc = result?.valid ? result.document : null;

  return (
    <div style={S.page} data-testid="verify-receipt-page">
      <div style={S.wrap}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: 4, color: "#D4AF37" }}>AYA</div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: "#9DB4A6", textTransform: "uppercase" }}>Vérification de reçu fiscal</div>
        </div>

        <div style={S.card}>
          <div style={{ fontSize: 12.5, color: "#9DB4A6", marginBottom: 10 }}>
            Saisissez le numéro du document (ex. RNE-CI-2026-000040) ou scannez le QR code imprimé sur le reçu.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={S.input}
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") verify(number); }}
              placeholder="Numéro du document…"
              data-testid="verify-number-input"
            />
            <button style={S.btn} onClick={() => verify(number)} disabled={loading} data-testid="verify-submit-btn">
              <Search size={14} /> Vérifier
            </button>
          </div>
          {!scanning ? (
            <button
              style={{ ...S.btn, width: "100%", justifyContent: "center", marginTop: 10, background: "rgba(212,175,55,0.14)", border: "1px solid rgba(212,175,55,0.45)", color: "#D4AF37" }}
              onClick={startScanner}
              data-testid="verify-scan-btn"
            >
              <Camera size={15} /> Scanner le QR code avec la caméra
            </button>
          ) : (
            <button
              style={{ ...S.btn, width: "100%", justifyContent: "center", marginTop: 10, background: "rgba(239,68,68,0.14)", border: "1px solid rgba(239,68,68,0.4)", color: "#F87171" }}
              onClick={stopScanner}
              data-testid="verify-scan-stop-btn"
            >
              <X size={15} /> Arrêter le scan
            </button>
          )}
          {scanning && (
            <div style={{ marginTop: 12 }} data-testid="verify-scanner-box">
              <div id="aya-qr-reader" style={{ width: "100%", borderRadius: 12, overflow: "hidden", border: "1px solid rgba(212,175,55,0.4)" }} />
              <div style={{ fontSize: 11, color: "#9DB4A6", textAlign: "center", marginTop: 6 }}>
                Visez le QR code imprimé sur le reçu 📄
              </div>
            </div>
          )}
          {scanError && (
            <div style={{ marginTop: 10, fontSize: 11.5, color: "#F5A623" }} data-testid="verify-scan-error">⚠️ {scanError}</div>
          )}
        </div>

        {loading && <div style={{ ...S.card, textAlign: "center", fontSize: 13, color: "#9DB4A6" }} data-testid="verify-loading">⏳ Vérification en cours…</div>}

        {!loading && result && !result.valid && (
          <div style={{ ...S.card, borderColor: "rgba(239,68,68,0.45)" }} data-testid="verify-invalid">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ShieldX size={26} color="#F87171" />
              <div>
                <div style={{ fontWeight: 800, fontSize: 14.5, color: "#F87171" }}>Document introuvable</div>
                <div style={{ fontSize: 12, color: "#9DB4A6", marginTop: 3 }}>
                  Ce numéro n'est pas enregistré dans le registre fiscal AYA. Le document n'est pas authentique ou le numéro est erroné.
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && doc && (
          <div style={{ ...S.card, borderColor: "rgba(52,211,153,0.5)" }} data-testid="verify-valid">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <ShieldCheck size={26} color="#34D399" />
              <div>
                <div style={{ fontWeight: 800, fontSize: 14.5, color: "#34D399" }}>Document authentique ✓</div>
                <div style={{ fontSize: 11.5, color: "#9DB4A6", marginTop: 2 }}>Enregistré dans le registre fiscal AYA.</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 10px", borderRadius: 999, background: TYPE_COLOR[doc.type] || "#475569", color: "#fff" }}>
                {doc.type === "CREDIT_NOTE" ? "AVOIR" : doc.type}
              </span>
              <span style={{ fontSize: 12, color: "#9DB4A6" }}>{TYPE_LABEL[doc.type] || doc.type}</span>
            </div>
            <div style={S.row}><span style={S.label}>Numéro</span><b data-testid="verify-doc-number">{doc.number}</b></div>
            <div style={S.row}><span style={S.label}>Vendeur</span><b>{doc.sellerName}</b></div>
            <div style={S.row}><span style={S.label}>Montant TTC</span><b>{fmt(doc.totalAmount)}</b></div>
            {Number.isFinite(doc.taxAmount) && doc.taxAmount > 0 && (
              <div style={S.row}><span style={S.label}>dont TVA</span><span>{fmt(doc.taxAmount)}</span></div>
            )}
            <div style={S.row}><span style={S.label}>Date d'émission</span><span>{String(doc.createdAt || "").slice(0, 16).replace("T", " à ")}</span></div>
            <div style={S.row}><span style={S.label}>Réf. certification</span><code style={{ fontSize: 10.5 }}>{doc.certificationReference || "—"}</code></div>
            {Array.isArray(doc.items) && doc.items.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 11.5, color: "#9DB4A6" }}>
                {doc.items.slice(0, 4).map((i, idx) => <div key={idx}>· {i.name} ×{i.qty}</div>)}
              </div>
            )}
            {doc.mode === "SANDBOX" && (
              <div style={{ marginTop: 12, padding: "8px 11px", borderRadius: 10, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)", fontSize: 11, color: "#F5A623", fontWeight: 700 }} data-testid="verify-sandbox-banner">
                ⚠️ Document émis en MODE SANDBOX — usage de test, non opposable à la DGI.
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 20, textAlign: "center", fontSize: 10.5, color: "#5B6B60" }}>
          AYA Fiscal Engine · Côte d'Ivoire
        </div>
      </div>
    </div>
  );
}
